import StatsWidget from '@rio-cloud/rio-uikit/StatsWidget';
import StatsWidgets from '@rio-cloud/rio-uikit/StatsWidgets';
import { useIntl } from 'react-intl';

const OVERDUE_AFTER_DAYS = 7;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

type InspectionOverviewItem = {
    criticalIssueCount: number;
    modificationTimestamp: number | string;
    totalDamages: number;
};

type OverviewTileProps = {
    footer?: string;
    icon: string;
    label: string;
    value: number;
    valueClassName: string;
};

type PDCOverviewTilesProps = {
    items: InspectionOverviewItem[];
};

const isOverdue = (item: InspectionOverviewItem, overdueThreshold: number) =>
    new Date(item.modificationTimestamp).getTime() < overdueThreshold;

const getOverviewMetrics = (items: InspectionOverviewItem[]) => {
    const overdueThreshold = Date.now() - OVERDUE_AFTER_DAYS * MILLISECONDS_PER_DAY;
    const metrics = { passed: 0, failed: 0, overdue: 0, immediateAttention: 0 };

    for (const item of items) {
        if (item.totalDamages === 0 && item.criticalIssueCount === 0) {
            metrics.passed += 1;
        }
        if (item.totalDamages > 0) {
            metrics.failed += 1;
        }
        if (isOverdue(item, overdueThreshold)) {
            metrics.overdue += 1;
        }
        if (item.criticalIssueCount > 0) {
            metrics.immediateAttention += 1;
        }
    }

    return metrics;
};

const OverviewTile = ({ footer, icon, label, value, valueClassName }: OverviewTileProps) => (
    <StatsWidget className='shadow-default display-flex flex-column'>
        <div className='display-flex flex-column flex-1-1'>
            <div className='flex-1-1 display-flex gap-15 padding-20'>
                <div className='height-50 aspect-ratio-1 bg-lightest rounded display-grid place-items-center text-size-14'>
                    <span className={`uikit-icon ${icon} text-size-20 ${valueClassName}`} aria-hidden='true' />
                </div>
                <div>
                    <div className='text-color-dark'>{label}</div>
                    <div className='text-size-h3 text-color-darkest line-height-125rel'>{value}</div>
                </div>
            </div>
            {footer && (
                <div className='text-size-12 padding-x-20 padding-y-10 bg-lightest rounded-bottom display-flex gap-5'>
                    <span className='text-color-dark'>{footer}</span>
                </div>
            )}
        </div>
    </StatsWidget>
);

const PDCOverviewTiles = ({ items }: PDCOverviewTilesProps) => {
    const { formatMessage } = useIntl();
    const metrics = getOverviewMetrics(items);

    return (
        <StatsWidgets className='display-grid grid-auto-fit-250 margin-bottom-20'>
            <OverviewTile
                icon='icon-error-sign'
                label={formatMessage({ id: 'intl-msg:pdc.overview.critical.label' })}
                value={metrics.immediateAttention}
                valueClassName='text-color-danger'
                footer={formatMessage({ id: 'intl-msg:pdc.overview.critical.footer' })}
            />
            <OverviewTile
                icon='icon-warning-sign'
                label={formatMessage({ id: 'intl-msg:pdc.overview.failed.label' })}
                value={metrics.failed}
                valueClassName='text-color-warning'
                footer={formatMessage({ id: 'intl-msg:pdc.overview.failed.footer' })}
            />
            <OverviewTile
                icon='icon-time'
                label={formatMessage({ id: 'intl-msg:pdc.overview.overdue.label' })}
                value={metrics.overdue}
                valueClassName='text-color-warning'
                footer={formatMessage({ id: 'intl-msg:pdc.overview.overdue.footer' })}
            />
            <OverviewTile
                icon='icon-check-badge'
                label={formatMessage({ id: 'intl-msg:pdc.overview.withoutIssues.label' })}
                value={metrics.passed}
                valueClassName='text-color-success'
                footer={formatMessage({ id: 'intl-msg:pdc.overview.withoutIssues.footer' })}
            />
        </StatsWidgets>
    );
};

export default PDCOverviewTiles;
